<template>
  <component :is="isComponent ? 'div' : 'UPage'">
    <ConfigHeader
      v-if="!isComponent"
      :back-to="`/customers/${customerNo}/tenants`"
      :last-editor="config.lastEditor"
      :last-sync="config.lastRagSync"
      :last-update="config.lastUpdate"
      :saving="saving"
      :show-sync="true"
      sync-label="RAG Sync"
      :syncing="syncing"
      :tenant-name="tenantName"
      title="Invoice Konfig"
      @save="saveConfig"
      @sync="syncInvoice"
    />

    <component :is="isComponent ? 'div' : 'UPageBody'" :class="{ 'p-0': isComponent }">
      <div v-if="loading" class="flex justify-center py-6">
        <UIcon class="w-8 h-8 animate-spin text-muted" name="i-lucide-loader-circle" />
      </div>

      <div v-else class="space-y-6">
        <!-- Status & Activation -->
        <ConfigStatusCard
          :model-value="config.active"
          :disabled="!canWrite"
          title="Rechnungs Automatisierung"
          description="Aktivieren oder deaktivieren Sie die Rechnungs-Anbindung für diesen Mandanten."
          icon="i-lucide-file-text"
          @update:model-value="onActiveToggle"
        />

        <USeparator class="my-8" />

        <ConfigChangelogAlert
          description="Bitte stellen Sie sicher, dass Ihre Invoice-Konfiguration die aktuellen Systemanforderungen erfüllt."
        />

        <!-- Einrichtungs-Informationen -->
        <UCard
          :ui="{
            root: isInfoOpen ? '' : 'divide-y-0',
            body: isInfoOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isInfoOpen = !isInfoOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-info" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Einrichtung und Konfiguration testen</h3>
              </div>
              <UButton
                :icon="isInfoOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isInfoOpen = !isInfoOpen"
              />
            </div>
          </template>

          <div v-show="isInfoOpen" class="space-y-6">
            <ConfigDocsAlert
              title="Einrichtung der Invoice Komponente"
              description="Folgen Sie bitte den Anweisungen in unserer Konfigurations-Dokumentation, um die Invoice-Komponente erfolgreich zu konfigurieren."
              doc-key="system-config.invoice.microtech-configuration"
              tenant-doc-key="tenant-config.invoice-portal-config"
            />

            <UAlert
              icon="i-lucide-lightbulb"
              title="Aktivierung"
              description="Es wird empfohlen, die Komponente erst nach einem erfolgreichen Konfigurationstest zu aktivieren."
              color="warning"
              variant="subtle"
              :ui="{
                title: 'text-warning-900 dark:text-warning-200',
                description: 'text-warning-800 dark:text-warning-300',
              }"
            />
          </div>
        </UCard>

        <!-- Empfänger Daten -->
        <UCard
          :ui="{
            root: isRecipientDataOpen ? '' : 'divide-y-0',
            body: isRecipientDataOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isRecipientDataOpen = !isRecipientDataOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-users" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Empfänger Daten</h3>
              </div>
              <UButton
                :icon="isRecipientDataOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isRecipientDataOpen = !isRecipientDataOpen"
              />
            </div>
          </template>

          <div v-show="isRecipientDataOpen" class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <UFormField label="Empfänger Name">
              <UInput v-model="config.recipientName" disabled placeholder="Kundenname" />
            </UFormField>
            <UFormField label="Empfänger Straße">
              <UInput v-model="config.recipientStreet" disabled placeholder="Straße" />
            </UFormField>
            <UFormField label="Empfänger Ort">
              <UInput v-model="config.recipientCity" disabled placeholder="Ort" />
            </UFormField>
            <UFormField label="Empfänger UST-ID">
              <UInput v-model="config.recipientVatId" disabled placeholder="z.B. DE123456789" />
            </UFormField>
            <UFormField label="Empfänger PLZ">
              <UInput v-model="config.recipientZip" disabled placeholder="PLZ" />
            </UFormField>
            <UFormField label="Empfänger Land">
              <UInput v-model="config.recipientCountry" disabled placeholder="Land" />
            </UFormField>
            <UFormField label="Empfänger IBAN">
              <UInput v-model="config.recipientIban" disabled placeholder="IBAN" />
            </UFormField>
            <UFormField label="Standard Kreditor">
              <template #label>
                <div class="flex items-center gap-1">
                  Standard Kreditor
                  <UTooltip
                    text="Falls der Belegaussteller in microtech nicht gefunden werden kann, wird der Eingangsbeleg unter dieser Adresse angelegt."
                  >
                    <UIcon class="w-4 h-4 text-gray-400" name="i-lucide-info" />
                  </UTooltip>
                </div>
              </template>
              <UInput v-model="config.fallbackVendorNo" :disabled="!canWrite" placeholder="z.B. 70000" />
            </UFormField>
          </div>
        </UCard>

        <!-- Mapping -->
        <UCard
          :ui="{
            root: isMappingOpen ? '' : 'divide-y-0',
            body: isMappingOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isMappingOpen = !isMappingOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-arrow-left-right" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">
                  Mapping E-Rechnung auf microtech Vorgangsarten
                </h3>
              </div>
              <UButton
                :icon="isMappingOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isMappingOpen = !isMappingOpen"
              />
            </div>
          </template>

          <div v-show="isMappingOpen" class="overflow-x-auto">
            <UTable :columns="invTypeColumns" :data="config.invTypes" class="w-full min-w-[600px] sm:min-w-0">
              <template #invType-cell="{ row }">
                <UInput v-model="row.original.invType" :disabled="!canWrite" placeholder="380" />
              </template>
              <template #invTypeName-cell="{ row }">
                <span class="text-sm text-muted italic">
                  {{ getInvTypeName(row.original.invType) }}
                </span>
              </template>
              <template #microtechTypeCode-cell="{ row }">
                <UInput v-model="row.original.microtechTypeCode" :disabled="!canWrite" placeholder="41" type="number" />
              </template>
              <template #actions-cell="{ row }">
                <UButton
                  v-if="canWrite"
                  color="error"
                  icon="i-lucide-trash"
                  variant="ghost"
                  @click="removeItem(config.invTypes, row.original)"
                />
              </template>
            </UTable>
            <UButton
              v-if="canWrite"
              class="mt-4"
              color="primary"
              icon="i-lucide-plus"
              label="Zeile hinzufügen"
              variant="outline"
              @click="config.invTypes.push({ invType: '', microtechTypeCode: null })"
            />
          </div>
        </UCard>

        <!-- Mapping Zahlungsarten -->
        <UCard
          :ui="{
            root: isPaymentMappingOpen ? '' : 'divide-y-0',
            body: isPaymentMappingOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isPaymentMappingOpen = !isPaymentMappingOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-credit-card" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Mapping Zahlungsarten</h3>
              </div>
              <UButton
                :icon="isPaymentMappingOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isPaymentMappingOpen = !isPaymentMappingOpen"
              />
            </div>
          </template>

          <div v-show="isPaymentMappingOpen" class="overflow-x-auto">
            <UTable
              :columns="paymentMethodColumns"
              :data="config.paymentMethodMappings"
              class="w-full min-w-[600px] sm:min-w-0"
            >
              <template #paymentMethodCode-cell="{ row }">
                <UInput v-model="row.original.paymentMethodCode" :disabled="!canWrite" placeholder="58" />
              </template>
              <template #paymentMethodName-cell="{ row }">
                <span class="text-sm text-muted italic">
                  {{ getPaymentMethodName(row.original.paymentMethodCode) }}
                </span>
              </template>
              <template #microtechId-cell="{ row }">
                <UInput v-model="row.original.microtechId" :disabled="!canWrite" placeholder="1" type="number" />
              </template>
              <template #actions-cell="{ row }">
                <UButton
                  v-if="canWrite"
                  color="error"
                  icon="i-lucide-trash"
                  variant="ghost"
                  @click="removeItem(config.paymentMethodMappings, row.original)"
                />
              </template>
            </UTable>
            <UButton
              v-if="canWrite"
              class="mt-4"
              color="primary"
              icon="i-lucide-plus"
              label="Zeile hinzufügen"
              variant="outline"
              @click="
                config.paymentMethodMappings.push({ paymentMethodCode: '', microtechId: null })
              "
            />
          </div>
        </UCard>

        <!-- Dokumentarten der Eingangsbelege -->
        <UCard
          :ui="{
            root: isDocumentTypesOpen ? '' : 'divide-y-0',
            body: isDocumentTypesOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isDocumentTypesOpen = !isDocumentTypesOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-files" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Dokumentarten der Eingangsbelege</h3>
              </div>
              <UButton
                :icon="isDocumentTypesOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isDocumentTypesOpen = !isDocumentTypesOpen"
              />
            </div>
          </template>

          <div v-show="isDocumentTypesOpen" class="space-y-6">
            <p class="text-sm text-muted">
              Legen Sie fest, unter welchen Dokumentarten Originalbelege und Zusatzdokumente im microtech DMS abgelegt werden.
            </p>

            <div class="space-y-4 max-w-xl">
              <UFormField label="Originalbeleg" description="Dokumentart für den primären Rechnungsbeleg">
                <USelect
                  v-model="config.originalDocumentType"
                  :disabled="!canWrite || loadingDocumentTypes"
                  :items="documentTypeOptions"
                  class="w-full"
                  label-key="label"
                  value-key="value"
                  placeholder="Dokumentart auswählen..."
                />
              </UFormField>

              <UFormField label="Zusatzdokumente" description="Dokumentart für zusätzliche Anhänge und Dokumente">
                <USelect
                  v-model="config.supplementaryDocumentType"
                  :disabled="!canWrite || loadingDocumentTypes"
                  :items="documentTypeOptions"
                  class="w-full"
                  label-key="label"
                  value-key="value"
                  placeholder="Dokumentart auswählen..."
                />
              </UFormField>
            </div>
          </div>
        </UCard>

        <!-- Ignorierte Artikel (Bestellvergleich) -->
        <UCard
          :ui="{
            root: isIgnoredArticlesOpen ? '' : 'divide-y-0',
            body: isIgnoredArticlesOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isIgnoredArticlesOpen = !isIgnoredArticlesOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-ban" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Ignorierte Artikel (Bestellvergleich)</h3>
              </div>
              <UButton
                :icon="isIgnoredArticlesOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isIgnoredArticlesOpen = !isIgnoredArticlesOpen"
              />
            </div>
          </template>

          <div v-show="isIgnoredArticlesOpen" class="space-y-4">
            <p class="text-sm text-muted">
              Artikelnummern, die beim automatischen Beleg- und Bestellvergleich ignoriert werden sollen (z. B. Fracht, Dienstleistungen, Nebenkosten). Sobald hier Artikel hinterlegt sind, überschreiben diese die Werte aus microtech-Regeln.
            </p>

            <div class="overflow-x-auto">
              <UTable
                :columns="ignoredArticleColumns"
                :data="ignoredArticleRows"
                class="w-full min-w-[600px] sm:min-w-0"
              >
                <template #articleNo-cell="{ row }">
                  <UInput
                    v-model="config.ignoredArticles[row.original.index]"
                    :disabled="!canWrite"
                    placeholder="z. B. FRACHT oder DL70000"
                    @update:model-value="
                      onArticleInput(row.original.index, config.ignoredArticles[row.original.index])
                    "
                    @blur="
                      onArticleBlur(row.original.index, config.ignoredArticles[row.original.index])
                    "
                  />
                </template>
                <template #articleKuBez-cell="{ row }">
                  <div class="flex items-center min-h-[36px] text-sm">
                    <template v-if="!config.ignoredArticles[row.original.index]?.trim()">
                      <span class="text-muted italic">-</span>
                    </template>
                    <template
                      v-else-if="
                        articleValidationMap[config.ignoredArticles[row.original.index]?.trim()]
                          ?.loading
                      "
                    >
                      <div class="flex items-center gap-1.5 text-muted">
                        <UIcon
                          name="i-lucide-loader-circle"
                          class="w-4 h-4 animate-spin text-primary"
                        />
                        <span class="text-xs">Wird geprüft...</span>
                      </div>
                    </template>
                    <template
                      v-else-if="
                        articleValidationMap[config.ignoredArticles[row.original.index]?.trim()]
                          ?.error
                      "
                    >
                      <div class="flex items-center gap-1.5 text-red-500">
                        <UIcon name="i-lucide-alert-circle" class="w-4 h-4" />
                        <span class="text-xs">Prüfung fehlgeschlagen</span>
                      </div>
                    </template>
                    <template
                      v-else-if="
                        articleValidationMap[config.ignoredArticles[row.original.index]?.trim()]
                          ?.notFound
                      "
                    >
                      <div class="flex items-center gap-1.5 text-amber-500">
                        <UIcon name="i-lucide-alert-triangle" class="w-4 h-4" />
                        <span class="text-xs font-medium">Artikel nicht gefunden</span>
                      </div>
                    </template>
                    <template
                      v-else-if="
                        articleValidationMap[config.ignoredArticles[row.original.index]?.trim()]
                          ?.kuBez !== undefined
                      "
                    >
                      <span
                        v-if="
                          articleValidationMap[config.ignoredArticles[row.original.index]?.trim()]
                            ?.kuBez
                        "
                        class="font-medium text-foreground"
                      >
                        {{
                          articleValidationMap[config.ignoredArticles[row.original.index]?.trim()]
                            ?.kuBez
                        }}
                      </span>
                      <span v-else class="italic text-muted text-xs">
                        (Keine Kurzbezeichnung)
                      </span>
                    </template>
                    <template v-else>
                      <span class="text-muted italic">-</span>
                    </template>
                  </div>
                </template>
                <template #actions-cell="{ row }">
                  <UButton
                    v-if="canWrite"
                    color="error"
                    icon="i-lucide-trash"
                    variant="ghost"
                    @click="removeIgnoredArticle(row.original.index)"
                  />
                </template>
              </UTable>
              <UButton
                v-if="canWrite"
                class="mt-4"
                color="primary"
                icon="i-lucide-plus"
                label="Artikel hinzufügen"
                variant="outline"
                @click="addIgnoredArticle"
              />
            </div>
          </div>
        </UCard>

        <!-- Konfigurationstest -->
        <UCard
          :ui="{
            root: isExpertValuesOpen ? '' : 'divide-y-0',
            body: isExpertValuesOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isExpertValuesOpen = !isExpertValuesOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-clipboard-check" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Konfigurationstest</h3>
              </div>
              <UButton
                :icon="isExpertValuesOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isExpertValuesOpen = !isExpertValuesOpen"
              />
            </div>
          </template>

          <div v-show="isExpertValuesOpen" class="space-y-6">
            <div
              class="flex flex-col sm:flex-row sm:items-center gap-6 p-4 rounded-lg border border-primary-200 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-900/20"
            >
              <div class="flex-1">
                <h4 class="font-bold">Konfiguration testen</h4>
                <p class="text-sm text-muted mt-1">
                  Überprüfen Sie, ob der Standardkreditor und alle eingetragenen Vorgangsarten im
                  Zielsystem existieren.
                </p>
              </div>
              <UButton
                :loading="testingExpertValues"
                color="primary"
                icon="i-lucide-zap"
                label="Test starten"
                @click="testExpertValues"
              />
            </div>

            <div
              v-if="expertTestResult"
              class="rounded-lg border p-4 transition-all duration-300"
              :class="
                expertTestResult.success
                  ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                  : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
              "
            >
              <div class="flex items-center gap-3 mb-6">
                <UIcon
                  :name="expertTestResult.success ? 'i-lucide-check-circle' : 'i-lucide-x-circle'"
                  :class="expertTestResult.success ? 'text-green-500' : 'text-red-500'"
                  class="w-8 h-8"
                />
                <span
                  :class="
                    expertTestResult.success
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-red-700 dark:text-red-300'
                  "
                  class="font-bold text-lg"
                >
                  {{ expertTestResult.message }}
                </span>
              </div>

              <div
                v-if="expertTestResult.checks"
                class="grid gap-4 sm:grid-cols-2 mt-6 border-t border-gray-200 dark:border-gray-800 pt-6"
              >
                <div
                  v-for="(check, index) in expertTestResult.checks"
                  :key="`${check.name}-${index}`"
                  class="flex gap-4 p-4 rounded-lg bg-white/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800"
                >
                  <div class="mt-0.5 shrink-0">
                    <UIcon
                      :name="
                        check.status === 'success'
                          ? 'i-lucide-check-circle'
                          : check.status === 'warning'
                            ? 'i-lucide-alert-circle'
                            : check.status === 'neutral'
                              ? 'i-lucide-help-circle'
                              : 'i-lucide-x-circle'
                      "
                      :class="
                        check.status === 'success'
                          ? 'text-green-500'
                          : check.status === 'warning'
                            ? 'text-orange-500'
                            : check.status === 'neutral'
                              ? 'text-gray-400'
                              : 'text-red-500'
                      "
                      class="w-5 h-5 flex-shrink-0"
                    />
                  </div>
                  <div>
                    <p class="font-bold text-gray-900 dark:text-white text-sm">{{ check.name }}</p>
                    <p class="text-gray-600 dark:text-gray-400 text-xs mt-1 leading-relaxed">
                      {{ check.info }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </UCard>
      </div>
    </component>

    <!-- Activation/Deactivation Confirmation Modal -->
    <ConfirmationModal
      v-model:open="isConfirmModalOpen"
      :description="
        config.active
          ? 'Bestätigen Sie die Deaktivierung der E-Rechnungs-Komponente.'
          : 'Bestätigen Sie die Aktivierung der E-Rechnungs-Komponente.'
      "
      :message="
        config.active
          ? 'Möchten Sie die E-Rechnungs-Komponente wirklich deaktivieren?'
          : !expertTestResult?.success
            ? 'Möchten Sie die E-Rechnungs-Komponente wirklich aktivieren? ACHTUNG: Der Konfigurationstest war nicht erfolgreich. Die Komponente funktioniert möglicherweise nicht korrekt.'
            : 'Möchten Sie die E-Rechnungs-Komponente wirklich aktivieren?'
      "
      :title="config.active ? 'E-Rechnung deaktivieren' : 'E-Rechnung aktivieren'"
      @confirm="confirmToggle"
    />
    <ConfirmationModal
      v-model:open="isConfirmSaveModalOpen"
      title="Konfiguration speichern"
      description="Bestätigen Sie das Speichern der Konfiguration."
      message="ACHTUNG: Der Konfigurationstest war nicht erfolgreich. Die Komponente funktioniert möglicherweise nicht korrekt. Möchten Sie die Konfiguration trotzdem speichern?"
      @confirm="confirmSave"
    />
  </component>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import axios from 'axios'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import { getPaymentMethodName } from '@/utils/paymentMethods'

interface InvoiceInvType {
  invType: string
  microtechTypeCode: number | null
}

interface PaymentMethodMapping {
  paymentMethodCode: string
  microtechId: number | null
}

interface InvoiceConfig {
  active: boolean
  recipientName: string
  recipientStreet: string
  recipientZip: string
  recipientCity: string
  recipientCountry: string
  recipientVatId: string
  recipientIban: string
  fallbackVendorNo: string
  invTypes: InvoiceInvType[]
  paymentMethodMappings: PaymentMethodMapping[]
  ignoredArticles: string[]
  originalDocumentType?: number
  supplementaryDocumentType?: number
  lastRagSync?: string
  lastUpdate?: string
  lastEditor?: string
}

interface DocumentTypeRow {
  fldNr: number
  fldBez: string
}

interface TestCheck {
  name: string
  status: 'success' | 'warning' | 'error' | 'neutral'
  info: string
}

interface TestResult {
  ok: boolean
  message: string
  checks: TestCheck[]
}

export interface ArticleValidationState {
  loading: boolean
  kuBez?: string | null
  notFound: boolean
  error: boolean
}

defineProps({
  customerNo: {
    type: String,
    required: true,
  },
  tenantName: {
    type: String,
    required: true,
  },
  isComponent: {
    type: Boolean,
    default: false,
  },
  canWrite: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['dirty'])

const toast = useToast()

const loading = ref(true)
const saving = ref(false)
const syncing = ref(false)
const isConfirmModalOpen = ref(false)
const isConfirmSaveModalOpen = ref(false)
const isInfoOpen = ref(false)
const isRecipientDataOpen = ref(false)
const isMappingOpen = ref(false)
const isPaymentMappingOpen = ref(false)
const isDocumentTypesOpen = ref(false)
const isIgnoredArticlesOpen = ref(false)
const isExpertValuesOpen = ref(false)
const testingExpertValues = ref(false)
const documentTypes = ref<DocumentTypeRow[]>([])
const loadingDocumentTypes = ref(false)

const documentTypeOptions = computed(() => {
  return documentTypes.value.map((dt) => ({
    label: `${dt.fldBez} (${dt.fldNr})`,
    value: dt.fldNr,
  }))
})

const expertTestResult = ref<{ success: boolean; message: string; checks?: TestCheck[] } | null>(
  null,
)

defineExpose({
  saveConfig: () => saveConfig(),
  syncInvoice: () => syncInvoice(),
  syncing,
})

const config = ref<InvoiceConfig>({
  active: false,
  recipientName: '',
  recipientStreet: '',
  recipientZip: '',
  recipientCity: '',
  recipientCountry: '',
  recipientVatId: '',
  recipientIban: '',
  fallbackVendorNo: '',
  invTypes: [],
  paymentMethodMappings: [],
  ignoredArticles: [],
  originalDocumentType: undefined,
  supplementaryDocumentType: undefined,
})

const invTypeColumns = [
  { id: 'invType', header: 'E-Rechnungscode' },
  { id: 'invTypeName', header: 'Bezeichnung' },
  { id: 'microtechTypeCode', header: 'microtech Nummer' },
  { id: 'actions', header: '' },
]

const paymentMethodColumns = [
  { id: 'paymentMethodCode', header: 'UNTDID 4461 Code' },
  { id: 'paymentMethodName', header: 'Bezeichnung' },
  { id: 'microtechId', header: 'microtech ID' },
  { id: 'actions', header: '' },
]

const ignoredArticleColumns = [
  { id: 'articleNo', header: 'Artikelnummer' },
  { id: 'articleKuBez', header: 'Kurzbezeichnung' },
  { id: 'actions', header: '' },
]

const ignoredArticleRows = computed(() => {
  return (config.value.ignoredArticles || []).map((_, index) => ({ index }))
})

const articleValidationMap = ref<Record<string, ArticleValidationState>>({})
const debounceTimers: Record<number, ReturnType<typeof setTimeout>> = {}

const validateArticle = async (articleNo: string, force = false) => {
  const trimmed = (articleNo || '').trim()
  if (!trimmed) {
    return
  }
  const cached = articleValidationMap.value[trimmed]
  if (!force && cached && !cached.error && !cached.loading) {
    return
  }

  articleValidationMap.value[trimmed] = {
    loading: true,
    kuBez: cached?.kuBez,
    notFound: false,
    error: false,
  }

  try {
    const response = await axios.get('/api/invoice/v1/articles/validate', {
      params: { articleNo: trimmed },
    })

    const data = response.data
    if (data?.exists) {
      articleValidationMap.value[trimmed] = {
        loading: false,
        kuBez: data.description ?? null,
        notFound: false,
        error: false,
      }
    } else {
      articleValidationMap.value[trimmed] = {
        loading: false,
        kuBez: null,
        notFound: true,
        error: false,
      }
    }
  } catch (error) {
    console.error(`Failed to validate article "${trimmed}"`, error)
    articleValidationMap.value[trimmed] = {
      loading: false,
      kuBez: null,
      notFound: false,
      error: true,
    }
  }
}

const onArticleInput = (index: number, value: string) => {
  if (debounceTimers[index]) {
    clearTimeout(debounceTimers[index])
  }
  debounceTimers[index] = setTimeout(() => {
    validateArticle(value)
    delete debounceTimers[index]
  }, 400)
}

const onArticleBlur = (index: number, value: string) => {
  if (debounceTimers[index]) {
    clearTimeout(debounceTimers[index])
    delete debounceTimers[index]
  }
  validateArticle(value)
}

const validateAllIgnoredArticles = async () => {
  if (!config.value.ignoredArticles || config.value.ignoredArticles.length === 0) {
    return
  }
  const uniqueArticles = Array.from(
    new Set(config.value.ignoredArticles.map((a) => (a || '').trim()).filter((a) => a.length > 0)),
  )
  await Promise.all(uniqueArticles.map((art) => validateArticle(art)))
}

const addIgnoredArticle = () => {
  if (!config.value.ignoredArticles) {
    config.value.ignoredArticles = []
  }
  config.value.ignoredArticles.push('')
}

const removeIgnoredArticle = (index: number) => {
  if (debounceTimers[index]) {
    clearTimeout(debounceTimers[index])
    delete debounceTimers[index]
  }
  if (config.value.ignoredArticles && index >= 0 && index < config.value.ignoredArticles.length) {
    config.value.ignoredArticles.splice(index, 1)
  }
}

const onActiveToggle = () => {
  isConfirmModalOpen.value = true
}

const confirmToggle = () => {
  config.value.active = !config.value.active
  isConfirmModalOpen.value = false
}

const confirmSave = () => {
  isConfirmSaveModalOpen.value = false
  saveConfig(true)
}

const fetchConfig = async () => {
  loading.value = true
  try {
    const response = await axios.get('/api/invoice/v1/config')
    const data = response.data
    config.value = {
      ...data,
      invTypes: data.invTypes || [],
      paymentMethodMappings: data.paymentMethodMappings || [],
      ignoredArticles: data.ignoredArticles || [],
      originalDocumentType: data.originalDocumentType ?? undefined,
      supplementaryDocumentType: data.supplementaryDocumentType ?? undefined,
    }

    // Ensure default codes 380, 384, 381 exist in invTypes
    const defaultCodes = [{ code: '380' }, { code: '384' }, { code: '381' }]

    defaultCodes.forEach((def) => {
      const exists = config.value.invTypes.some((t) => t.invType === def.code)
      if (!exists) {
        config.value.invTypes.push({
          invType: def.code,
          microtechTypeCode: null,
        })
      }
    })

    await validateAllIgnoredArticles()
  } catch (error) {
    console.error('Failed to fetch E-Invoice config', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Laden der Konfiguration.',
      color: 'danger',
    })
  } finally {
    loading.value = false
  }
}

const fetchDocumentTypes = async () => {
  loadingDocumentTypes.value = true
  try {
    const response = await axios.get('/api/invoice/v1/document-types')

    const rows = response.data
    if (Array.isArray(rows)) {
      documentTypes.value = rows
    } else {
      documentTypes.value = []
    }
  } catch (error) {
    console.error('Failed to fetch document types', error)
    documentTypes.value = []
  } finally {
    loadingDocumentTypes.value = false
  }
}

const getPaymentMethodMappingError = () => {
  const paymentMethodCodes = new Set<string>()

  for (const mapping of config.value.paymentMethodMappings) {
    const paymentMethodCode = mapping.paymentMethodCode.trim()
    if (!paymentMethodCode || mapping.microtechId === null) {
      return 'Alle Zahlungsarten-Mappings müssen vollständig ausgefüllt sein.'
    }
    if (paymentMethodCodes.has(paymentMethodCode)) {
      return `Der Zahlungscode ${paymentMethodCode} ist mehrfach zugeordnet.`
    }
    paymentMethodCodes.add(paymentMethodCode)
  }

  return null
}

const saveConfig = async (force = false) => {
  const paymentMethodMappingError = getPaymentMethodMappingError()
  if (paymentMethodMappingError) {
    toast.add({ title: 'Ungültiges Mapping', description: paymentMethodMappingError, color: 'danger' })
    return
  }
  if (!force && config.value.active && expertTestResult.value?.success === false) {
    isConfirmSaveModalOpen.value = true
    return
  }
  saving.value = true
  try {
    const seen = new Set<string>()
    const sanitizedIgnoredArticles: string[] = []
    for (const art of config.value.ignoredArticles || []) {
      const trimmed = art.trim()
      if (trimmed && !seen.has(trimmed)) {
        seen.add(trimmed)
        sanitizedIgnoredArticles.push(trimmed)
      }
    }
    config.value.ignoredArticles = sanitizedIgnoredArticles

    await axios.post('/api/invoice/v1/config', config.value)
    toast.add({ title: 'Erfolg', description: 'Konfiguration gespeichert.', color: 'success' })
    await fetchConfig()
  } catch (error) {
    console.error('Failed to save E-Invoice config', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Speichern der Konfiguration.',
      color: 'danger',
    })
  } finally {
    saving.value = false
  }
}

const syncInvoice = async () => {
  syncing.value = true
  try {
    await axios.post('/api/invoice/v1/sync')
    toast.add({
      title: 'Erfolg',
      description: 'RAG Synchronisation wurde getriggert.',
      color: 'success',
    })
  } catch (error) {
    console.error('Failed to trigger RAG sync', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Triggern der RAG Synchronisation.',
      color: 'danger',
    })
  } finally {
    syncing.value = false
  }
}

const testExpertValues = async () => {
  testingExpertValues.value = true
  expertTestResult.value = null
  try {
    const response = await axios.post<TestResult>('/api/invoice/v1/config/validate', {
      fallbackVendorNo: config.value.fallbackVendorNo || '',
      invTypes: config.value.invTypes,
      paymentMethodMappings: config.value.paymentMethodMappings,
      originalDocumentType: config.value.originalDocumentType,
      supplementaryDocumentType: config.value.supplementaryDocumentType,
      ignoredArticles: config.value.ignoredArticles,
    })
    expertTestResult.value = {
      success: response.data.ok,
      message: response.data.message,
      checks: response.data.checks,
    }
  } catch (error: unknown) {
    let msg = 'Unknown error'
    let checks: TestCheck[] | undefined = undefined
    if (axios.isAxiosError(error)) {
      if (error.response?.data && typeof error.response.data === 'object') {
        msg = error.response.data.message || error.message
        checks = error.response.data.checks
      } else {
        msg = error.response?.data || error.message
      }
    } else if (error instanceof Error) {
      msg = error.message
    }
    expertTestResult.value = { success: false, message: `Fehler: ${msg}`, checks }
  } finally {
    testingExpertValues.value = false
  }
}

const getInvTypeName = (code: string) => {
  const names: Record<string, string> = {
    '380': 'Eingangsrechnung',
    '381': 'Gutschrift vom Lieferanten',
    '384': 'Eingangsrechnungskorrektur',
  }
  return names[code] || ''
}

const removeItem = <T,>(list: T[], item: T) => {
  const index = list.indexOf(item)
  if (index > -1) {
    list.splice(index, 1)
  }
}

watch(
  config,
  () => {
    emit('dirty')
  },
  { deep: true },
)

onMounted(() => {
  fetchConfig()
  fetchDocumentTypes()
})
</script>
